import PocketBase, { RecordModel } from 'pocketbase';
import { UserRole } from '@/utils/roleValidation';
import { logger } from '@/utils/secureLogger';

// PocketBase API URL
const pocketbaseUrl = 'https://api.tutornesta.com/';

/**
 * CRITICAL SECURITY WARNING:
 * This PocketBase instance is a SINGLETON - it's shared globally across the entire application.
 * 
 * DO NOT manipulate pb.authStore directly in utility functions or auto-approval code!
 * Only auth-related hooks (useAuth) should update the auth store.
 * 
 * Why? Because if you do pb.authStore.save() with User A's data while User B is logged in,
 * you'll replace User B's session with User A's session, causing serious security issues.
 * 
 * Safe pattern:
 * - Read from pb.authStore: ✅ OK
 * - Update database records: ✅ OK
 * - Call pb.authStore.save(): ❌ ONLY in useAuth hook during login/register
 */
export const pb = new PocketBase(pocketbaseUrl);

// Real-time is enabled for messages, voice_notes, and other collections
// The PocketBase server supports real-time subscriptions

// Disable polling override that causes performance issues
// const originalCollection = pb.collection.bind(pb);
// pb.collection = function(name: string) {
//   const collection = originalCollection(name);
//   // Polling removed for performance - components should use react-query instead
//   return collection;
// };

// Enable auto cancellation prevention
pb.autoCancellation(false);

// Secure auth state change logging
pb.authStore.onChange((token, model) => {
  logger.debug('Auth state changed', {
    hasToken: !!token,
    hasModel: !!model,
    isValid: pb.authStore.isValid,
    userId: model?.id || null,
    userRole: model?.role || null
  });
});

// Request interceptor with secure logging
pb.beforeSend = function (url, options) {
  logger.debug('PocketBase request', {
    url: url.replace(pocketbaseUrl, '[HOST]'), // Hide actual host
    method: options.method || 'GET',
    hasAuth: !!pb.authStore.token,
    isValidAuth: pb.authStore.isValid
  });
  
  // Don't manually set auth headers - PocketBase handles this automatically
  // Suppress expected auth warnings for public data collections
  const publicCollections = [
    'tutors', 'subjects', 'reviews', 'focusPages', 'tutor_online_status', 
    'lesson_library', 'users', 'approved_subjects', 'liveChatAgents', 
    'liveChatSessions', 'liveChatMessages', 'local_fitness_coaches', 'local_music_coaches',
    'local_travel_guides', 'notifications', 'conversations', 'messages', 'subject_requests',
    'tutor_student_messages', 'announcements', 'travel_pages', 'travel_stories', 'success_stories'
  ];
  const isPublicCollection = publicCollections.some(collection => url.includes(`/collections/${collection}/`));
  
  if (!pb.authStore.isValid && url.includes('/api/') && !isPublicCollection) {
    logger.warn('API request without valid auth token', {
      url: url.replace(pocketbaseUrl, '[HOST]')
    });
  }
  
  return { url, options };
};

// Types for our collections
export interface User extends RecordModel {
  email: string;
  name: string;
  role: UserRole;
  status?: 'active' | 'pending' | 'suspended';
  avatar?: string;
}

export interface AuthUser extends User {
  verified: boolean;
}

export interface IceServer extends RecordModel {
  urls: string;
  username?: string;
  credential?: string;
  isActive: boolean;
  serverType: 'stun' | 'turn';
  priority: number;
  description?: string;
}

export interface Review extends RecordModel {
  tutorId: string; // Reference to tutor profile ID
  studentId: string; // Reference to user ID
  rating: number; // 1-5 star rating
  comment: string;
  subject: string;
  isVerified: boolean;
  expand?: {
    studentId?: User;
    tutorId?: TutorProfile;
  };
}

export interface Announcement extends RecordModel {
  title: string;
  content: string;
  isActive: boolean;
  priority: number;
  image?: string;
  createdBy: string;
}

export interface Education {
  degree: string;
  institution: string;
  year: number;
}

export interface TimeSlot {
  start: string;
  end: string;
}

export interface DaySlot {
  day: string;
  slots: TimeSlot[];
}

export interface Availability {
  days: string[];
  timeSlots: DaySlot[];
}

export interface TutorProfile extends RecordModel {
  user: string; // Reference to user ID
  name?: string; // Tutor's name
  bio: string;
  shortBio: string;
  subjects: string[];
  hourlyRate: number;
  // Additional pricing fields
  sessionRate?: number; // Per session rate
  levelRates?: {
    beginner: number;
    intermediate: number;
    advanced: number;
  }; // Level-based rates
  monthlyRate?: number; // Monthly subscription rate
  pricingType?: 'hourly' | 'session' | 'level' | 'monthly'; // Primary pricing type
  pricingCurrency?: string; // Currency for pricing
  languages: string[];
  experience: string;
  education: Education[];
  availability: Availability;
  introVideo?: string;
  profilePicture?: string; // For file uploads
  profilePictureUrl?: string; // For URL storage
  country?: string;
  rating?: number;
  reviewCount?: number;
  isFeatured?: boolean;
  personalEmail?: string; // Admin-only personal email
  phoneNumber?: string; // Admin-only phone number
  adminNotes?: string; // Admin-only notes field
  // New dynamic fields
  responseTimeHours?: number; // Response time in hours
  totalLessons?: number; // Total lessons taught
  isOnline?: boolean; // Current online status
  lastSeen?: string; // Last seen timestamp
  // New hero tutor fields
  isHeroGuide?: boolean; // Whether this tutor is a hero guide
  heroCountries?: string[]; // Array of country codes where this tutor is hero
  // New hero guide customization fields
  heroIntroduction?: string; // Custom introduction for hero sections
  heroSpecialties?: string[]; // Specific specialties for hero display
  heroExperience?: string; // Custom experience text for hero sections
  sessionPreference?: 'chat_only' | 'video_only' | 'both'; // Session type preference
  instant_session_approved?: boolean; // Admin approval for instant sessions
  instant_session_notes?: string; // Admin notes about instant session approval
  paymentMethods?: string[]; // Array of accepted payment method IDs
  isFrozen?: boolean; // Whether the tutor has frozen their account
  expand?: {
    user?: User;
  };
}

// Add TravelPage interface for travel pages collection
export interface TravelPage extends RecordModel {
  countryCode: string;
  countryName: string;
  flag: string;
  heroTitle: string;
  heroDescription: string;
  featuredGuideId?: string;
  featuredGuideName: string;
  featuredGuideImage: string;
  heroBackgroundImage: string;
  moreGuides: {
    id: string;
    name: string;
    photo: string;
    rating: number;
    reviewCount: number;
    hourlyRate: number;
    location: string;
    specialties: string[];
  }[];
  services: {
    id: string;
    type: string;
    title: string;
    description: string;
    contact: string;
    email: string;
    location: string;
    rating: number;
    icon: string;
  }[];
  isActive: boolean;
}

export interface StudentProfile extends RecordModel {
  user: string; // Reference to user ID
  name: string;
  bio?: string;
  grade?: string;
  subjects: string[];
  learningGoals?: string;
  preferredLearningStyle?: string;
  parentEmail?: string;
  emergencyContact?: string;
  personalEmail?: string; // Admin-only personal email
  phoneNumber?: string; // Admin-only phone number
  adminNotes?: string; // Admin-only notes field
  expand?: {
    user?: User;
  };
}

export interface Booking extends RecordModel {
  studentId: string; // Reference to user ID
  tutorId: string; // Reference to tutor profile ID
  date: string;
  startTime: string;
  endTime: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'pending_payment';
  subject: string;
  studentEmail: string; // Student's email for tutor communication
  studentPhone?: string; // Optional student phone number
  studentCountry?: string; // Student's country code
  studentTimezone?: string; // Student's timezone
  tutorTimezone?: string; // Tutor's timezone (fetched from tutor profile)
  notes?: string;
  was_rescheduled?: boolean; // Indicates if the booking was rescheduled
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded' | 'free';
  price: number;
  stripeSessionId?: string;
  expand?: {
    studentId?: User;
    tutorId?: TutorProfile;
  };
}

export interface PaymentSession extends RecordModel {
  bookingId: string; // Reference to booking ID
  stripeSessionId: string;
  status: 'pending' | 'completed' | 'expired' | 'cancelled';
  amount: number;
  currency: string;
}

export interface Lesson extends RecordModel {
  studentId: string; // Reference to user ID
  tutorId: string; // Reference to user ID
  bookingId?: string; // Reference to booking ID
  title: string;
  description?: string;
  subject: string;
  date: string;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  notes?: string;
  materials?: string;
  recordingUrl?: string;
  expand?: {
    studentId?: User;
    tutorId?: User;
  };
}

export interface SessionConnection extends RecordModel {
  bookingId: string;
  peerId: string;
  role: 'tutor' | 'student';
  userName: string;
  status: 'waiting' | 'connected' | 'disconnected';
  lastSeen: string;
}

export interface SessionSignal extends RecordModel {
  bookingId: string;
  fromPeerId: string;
  toPeerId: string;
  signalType: 'offer' | 'answer' | 'ice-candidate' | 'chat' | 'screen-share-start' | 'screen-share-stop';
  signalData: any;
  delivered: boolean;
}

export interface Conversation extends RecordModel {
  studentId: string; // Reference to user ID
  tutorId: string; // Reference to user ID
  lastMessage: string;
  lastMessageAt: string;
  status: 'active' | 'read' | 'archived'; // New field for conversation status
  unreadCount: number; // New field for unread message count
  expand?: {
    studentId?: User;
    tutorId?: User;
  };
}

export interface Message extends RecordModel {
  conversationId: string; // Reference to conversation ID
  senderId: string; // Reference to user ID
  receiverId: string; // Reference to user ID
  message: string;
  isRead: boolean;
  messageType: 'text' | 'image' | 'file'; // New field for message type
  attachmentUrl?: string; // New field for file attachments
  expand?: {
    senderId?: User;
    receiverId?: User;
    conversationId?: Conversation;
  };
}

// New Live Chat Support System interfaces
export interface LiveChatAgent extends RecordModel {
  user: string; // Reference to user ID (tutor or admin)
  name: string;
  email: string;
  status: 'online' | 'offline' | 'busy';
  isActive: boolean; // Admin can activate/deactivate agents
  maxConcurrentChats: number;
  currentChatCount: number;
  specialties: string[]; // Areas of expertise
  expand?: {
    user?: User;
  };
}

export interface LiveChatSession extends RecordModel {
  visitorName: string;
  visitorEmail?: string;
  agentId?: string; // Reference to LiveChatAgent ID
  status: 'waiting' | 'assigned' | 'active' | 'closed';
  subject?: string;
  priority: 'low' | 'normal' | 'high';
  startedAt: string;
  assignedAt?: string;
  closedAt?: string;
  rating?: number; // 1-5 star rating from visitor
  feedback?: string;
  channel?: 'contact-form' | 'live-chat';
  expand?: {
    agentId?: LiveChatAgent;
  };
}

export interface LiveChatMessage extends RecordModel {
  sessionId: string; // Reference to LiveChatSession ID
  senderId?: string; // Reference to user ID (for agents)
  senderName: string; // Name of sender (visitor name or agent name)
  senderType: 'visitor' | 'agent' | 'system';
  message: string;
  messageType: 'text' | 'image' | 'file' | 'system';
  attachmentUrl?: string;
  isRead: boolean;
  expand?: {
    sessionId?: LiveChatSession;
    senderId?: User;
  };
}

export interface LiveChatQueue extends RecordModel {
  sessionId: string; // Reference to LiveChatSession ID
  priority: 'low' | 'normal' | 'high';
  waitingSince: string;
  estimatedWaitTime?: number; // in minutes
  position: number; // Position in queue
  expand?: {
    sessionId?: LiveChatSession;
  };
}

export interface ApprovedSubject extends RecordModel {
  name: string;
  isActive: boolean;
  approvedAt: string;
  approvedBy: string; // Admin user ID
  expand?: {
    approvedBy?: User;
  };
}

export interface SubjectRequest extends RecordModel {
  tutorId: string; // Reference to user ID
  tutorName: string;
  tutorEmail: string;
  subjectName: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  reviewedAt?: string;
  reviewedBy?: string; // Admin user ID
  reviewNotes?: string;
  expand?: {
    tutorId?: User;
    reviewedBy?: User;
  };
}

export interface Notification extends RecordModel {
  userId: string; // Reference to user ID
  type: 'subject_approved' | 'subject_rejected' | 'profile_approved' | 'profile_rejected' | 
        'new_message' | 'message_received' | 
        'booking_confirmed' | 'booking_cancelled' | 'booking_reminder' | 
        'lesson_reminder' | 'lesson_completed' | 
        'tutor_approved' | 'tutor_rejected' | 
        'student_registered' | 
        'payment_received' | 'payment_failed' | 
        'announcement' | 'system_update' | 'general' |
        // Admin-specific types - simplified
        'admin_notification';
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  data?: any; // Optional additional data
  expand?: {
    userId?: User;
  };
}

export interface Subject extends RecordModel {
  name: string;
  category: 'focus' | 'default' | 'approved';
  isActive: boolean;
  icon?: string;
  description?: string;
  displayOrder?: number;
}

export interface TravelStory extends RecordModel {
  title: string;
  caption: string;
  location?: string;
  tags: string[];
  images: string[]; // Array of image filenames
  tutorId: string; // Reference to tutor profile ID
  likes: number;
  views: number;
  isPublished: boolean;
  expand?: {
    tutorId?: TutorProfile;
  };
}

export interface LocalTravelGuide extends RecordModel {
  tutorId: string; // Reference to tutor profile ID
  name: string;
  email: string;
  phone?: string;
  bio: string;
  specialties: string[]; // e.g., "Cultural Tours", "Food Tours", "Adventure Travel", "Historical Sites"
  hourlyRate: number;
  location: {
    city: string;
    state: string;
    country: string;
    zipCode: string;
  };
  profileImage?: string;
  profileImageUrl?: string;
  experience: number; // Years of experience
  rating: number;
  reviewCount: number;
  coverageRadius: number; // Coverage radius in miles/km
  isActive: boolean;
  isVerified: boolean;
  isFeatured?: boolean;
  acceptsGroupTours?: boolean;
  languages: string[]; // Languages spoken
  maxGroupSize?: number; // Maximum group size for tours
  expand?: {
    tutorId?: TutorProfile;
  };
}

// Shop/E-commerce interfaces
export interface ShopProduct extends RecordModel {
  title: string;
  description: string;
  price: number;
  category: string; // e.g., "books", "videos", "worksheets", "tools"
  subject: string; // e.g., "math", "english", "programming"
  sellerType: 'admin' | 'tutor';
  sellerId?: string; // NULL for admin, tutor profile ID for tutors
  status: 'active' | 'inactive' | 'pending';
  images: string[]; // Array of uploaded image filenames
  imageUrls?: string[]; // Array of image URLs
  fileUrl?: string; // For downloadable materials
  stockQuantity: number; // -1 for unlimited
  isDigital: boolean;
  expand?: {
    sellerId?: TutorProfile;
  };
}

export interface ShopOrder extends RecordModel {
  userId: string; // Reference to user ID
  totalAmount: number;
  status: 'pending' | 'paid' | 'shipped' | 'delivered' | 'cancelled';
  stripeSessionId?: string;
  expand?: {
    userId?: User;
  };
}

export interface ShopOrderItem extends RecordModel {
  orderId: string; // Reference to shop order ID
  productId: string; // Reference to shop product ID
  quantity: number;
  price: number; // Price at time of purchase
  expand?: {
    orderId?: ShopOrder;
    productId?: ShopProduct;
  };
}

// Interactive Story Elements Collections

export interface StoryPoll extends RecordModel {
  storyId: string; // Reference to travel story ID
  question: string;
  options: string[]; // Array of poll options
  votes: { [option: string]: number }; // Object mapping option to vote count
  totalVotes: number;
  isActive: boolean;
  allowMultipleVotes: boolean;
  expiresAt?: string; // Optional expiration date
  expand?: {
    storyId?: TravelStory;
  };
}

export interface StoryPollVote extends RecordModel {
  pollId: string; // Reference to story poll ID
  userId?: string; // Reference to user ID (null for anonymous)
  option: string; // The selected option
  voterHash: string; // Hash to prevent duplicate anonymous votes
  expand?: {
    pollId?: StoryPoll;
    userId?: User;
  };
}

export interface StorySoundInteraction extends RecordModel {
  storyId: string; // Reference to travel story ID
  soundType: 'ambient' | 'music' | 'voice' | 'effect'; // Type of sound
  soundUrl: string; // URL to the sound file
  title: string; // Sound title/description
  duration?: number; // Duration in seconds
  isAutoplay: boolean;
  volume: number; // 0-100
  position: {
    x: number; // Position on image (percentage)
    y: number;
  };
  triggerType: 'click' | 'hover' | 'auto'; // How sound is triggered
  isActive: boolean;
  expand?: {
    storyId?: TravelStory;
  };
}

export interface StoryInteractiveElement extends RecordModel {
  storyId: string; // Reference to travel story ID
  elementType: 'quiz' | 'rating' | 'comment' | 'bookmark' | 'share' | 'tip' | 'ar_marker'; // Type of interactive element
  title: string;
  description?: string;
  config: any; // Configuration object for the element
  position: {
    x: number; // Position on image (percentage)
    y: number;
  };
  isActive: boolean;
  expand?: {
    storyId?: TravelStory;
  };
}

export interface StoryQuizResponse extends RecordModel {
  elementId: string; // Reference to story interactive element ID
  userId?: string; // Reference to user ID (null for anonymous)
  answers: any; // User's quiz answers
  score?: number; // Quiz score if applicable
  completedAt: string;
  expand?: {
    elementId?: StoryInteractiveElement;
    userId?: User;
  };
}

export interface StoryRating extends RecordModel {
  storyId: string; // Reference to travel story ID
  userId?: string; // Reference to user ID (null for anonymous)
  rating: number; // 1-5 star rating
  comment?: string; // Optional comment
  raterHash: string; // Hash to prevent duplicate anonymous ratings
  expand?: {
    storyId?: TravelStory;
    userId?: User;
  };
}

export interface StoryComment extends RecordModel {
  storyId: string; // Reference to travel story ID
  userId?: string; // Reference to user ID (null for anonymous)
  parentId?: string; // Reference to parent comment (for replies)
  content: string;
  authorName: string; // Display name (for anonymous users)
  isApproved: boolean; // For moderation
  likesCount: number;
  expand?: {
    storyId?: TravelStory;
    userId?: User;
    parentId?: StoryComment;
  };
}

export interface StoryBookmark extends RecordModel {
  storyId: string; // Reference to travel story ID
  userId: string; // Reference to user ID
  notes?: string; // Optional personal notes
  expand?: {
    storyId?: TravelStory;
    userId?: User;
  };
}

export interface StoryShare extends RecordModel {
  storyId: string; // Reference to travel story ID
  userId?: string; // Reference to user ID (null for anonymous)
  platform: 'facebook' | 'twitter' | 'instagram' | 'email' | 'link' | 'whatsapp';
  sharedAt: string;
  expand?: {
    storyId?: TravelStory;
    userId?: User;
  };
}

export interface StoryTip extends RecordModel {
  storyId: string; // Reference to travel story ID
  giverId?: string; // Reference to user ID (null for anonymous)
  recipientId: string; // Reference to story author (tutor)
  amount: number; // Tip amount
  currency: string; // Currency code
  message?: string; // Optional message
  paymentStatus: 'pending' | 'completed' | 'failed';
  stripePaymentId?: string;
  expand?: {
    storyId?: TravelStory;
    giverId?: User;
    recipientId?: User;
  };
}

export interface StoryAnalytics extends RecordModel {
  storyId: string; // Reference to travel story ID
  eventType: 'view' | 'like' | 'share' | 'comment' | 'poll_vote' | 'sound_play' | 'quiz_complete';
  userId?: string; // Reference to user ID (null for anonymous)
  sessionId: string; // Session identifier for anonymous tracking
  metadata?: any; // Additional event data
  userAgent?: string; // Browser/device info
  expand?: {
    storyId?: TravelStory;
    userId?: User;
  };
}

export interface PaymentMethodSetting extends RecordModel {
  methodId: string; // Unique identifier for payment method (e.g., 'fawry', 'instapay')
  methodName: string; // Display name for payment method
  isEnabled: boolean; // Whether this payment method is enabled
  createdBy: string; // Admin user ID who created this setting
  updatedBy: string; // Admin user ID who last updated this setting
  createdAt: string;
  updatedAt: string;
  expand?: {
    createdBy?: User;
    updatedBy?: User;
  };
}

// Inspire Collections
export interface InspirePost extends RecordModel {
  tutor_id: string; // Reference to tutor profile ID
  content: string;
  image?: string; // Optional single image filename (legacy support)
  images?: string[]; // Optional array of image filenames for multi-photo posts
  video?: string; // Optional video filename
  likes_count: number; // Calculated from inspire_likes collection
  comments_count: number; // Calculated from inspire_comments collection
  shares_count?: number; // Calculated from inspire_post_shares collection (optional for compatibility)
  is_published: boolean;
  expand?: {
    tutor_id?: TutorProfile;
  };
}

export interface InspireComment extends RecordModel {
  post_id: string; // Reference to inspire post ID
  user_id: string; // Reference to user ID
  content: string;
  expand?: {
    post_id?: InspirePost;
    user_id?: User;
  };
}

export interface InspireLike extends RecordModel {
  post_id: string; // Reference to inspire post ID
  user_id: string; // Reference to user ID
  expand?: {
    post_id?: InspirePost;
    user_id?: User;
  };
}

export interface InspirePostShare extends RecordModel {
  post_id: string; // Reference to inspire post ID
  user_id?: string; // Optional - allows anonymous shares
  expand?: {
    post_id?: InspirePost;
    user_id?: User;
  };
}

// Form System Interfaces
export interface FormField {
  id?: string;
  name?: string; // Alternative to id
  label?: string;
  question?: string; // Alternative to label for quiz-style forms
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'number' | 'date';
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

export interface FormTemplate extends RecordModel {
  title: string;
  description?: string;
  fields: FormField[];
  created_by: string;
  is_active: boolean;
  scoring?: {
    correctAnswers: Record<string, string>;
    levels: Array<{
      range: string; // e.g., "0-3"
      label: string;
    }>;
  };
}

export interface FormAssignment extends RecordModel {
  form_template: string;
  tutor_id: string;
  student_id: string;
  message_id?: string;
  status: 'pending' | 'completed' | 'viewed';
  due_date?: string;
  notes?: string;
  evaluation_visible?: boolean; // Controls if student can view evaluation
  expand?: {
    form_template?: FormTemplate;
    tutor_id?: User;
    student_id?: User;
  };
}

export interface FormResponse extends RecordModel {
  assignment_id: string;
  student_id: string;
  responses: Record<string, any>;
  submitted_at?: string;
  is_draft: boolean;
  expand?: {
    assignment_id?: FormAssignment;
  };
}
